/**
 * Google Search Console keyword import used by the audit onboarding "Connect Google" step
 * (White Glove `GoogleDataStep.vue` and the POP Authority `GoogleStep.vue`).
 *
 * Pulls QUERY+PAGE rows through `gapi` for the selected property / period, computes
 * cannibalization and page metrics, saves them as SITE_ONLY_EXIST / PAGE_OPPORTUNITY keyword
 * wizard rows on the site's domain-level campaign, stores the Trust & Authority score history,
 * then marks the audit `hasRetrievedFreshGSCData: 1` and emits `success` / `close`.
 *
 * Host component contract:
 * - props `site` and `activeAudit`
 * - `gapi` must hold a valid Search Console token (browser GoogleLogin or restoreBackendGoogleSession)
 * - selections come from the store `insightKeywordsConfig` (wizardLocation / wizardLanguage /
 *   wizardDomain / wizardIsCountryEnabled / wizardPeriod / wizardIsBrandedEnabled / wizardBrandedText)
 * - call `loadDomainLevelCampaign()` before `onStart()`
 */
import { gapi } from 'gapi-script'
import { groupBy, forEach, sumBy, maxBy, meanBy } from 'lodash'

import { getHostName } from '@/utils'
import { getCountryCode_ISO3 } from '@/utils/search-console-helper.js'
import { DATE_RANGE_OPTIONS, getOptionDate } from '@/components/Campaigns/keyword-wizard/DateRangeSelect.vue'

export default {
  data () {
    return {
      googleSiteUrl: '',
      hasActiveWinsConnection: false,
      googleConsoleDataAggregated: [],
      submitting_console_data: false,
      submitting_pop_impact_score: false,
    }
  },
  computed: {
    user () {
      if (this.$store.state.user) {
        return this.$store.state.user
      }
      return null
    },
    selectedSubAccountRelation () {
      return this.$store.state.selectedSubAccountRelation
    },
    config () {
      return this.$store.getters.insightKeywordsConfig
    },
    submitting () {
      return this.submitting_pop_impact_score || this.submitting_console_data
    },
    availablePeriods () {
      return DATE_RANGE_OPTIONS
    },
    campaign () {
      return this.$store.state.currentCampaign
    },
    brandedTerms () {
      return (this.config.wizardBrandedText || '')
        .replace(/\n/g, ',')
        .split(',')
        .reduce((acc, cur) => {
          if (cur && cur.trim()) {
            acc.push(cur.trim())
          }
          return acc
        }, [])
    },
  },
  methods: {
    emitReadiness () {
      this.$nextTick(() => {
        this.$emit('readiness-change')
      })
    },
    setConfig (key, value) {
      this.$store.commit('setInsightKeywordsConfig', { key, value })
    },
    getErrorMessage (error, fallback = 'Could not save results.') {
      if (typeof error === 'string' && error.trim()) return error

      const possibleMessages = [
        error?.response?.data?.msg,
        error?.response?.data?.message,
        error?.data?.msg,
        error?.data?.message,
        error?.message,
      ]

      const message = possibleMessages.find((value) => typeof value === 'string' && value.trim())
      return message || fallback
    },
    estimatePayloadSize (payload) {
      try {
        return JSON.stringify(payload).length
      } catch (error) {
        return 0
      }
    },
    buildSaveChunks (rows, { maxChunkRows = 2500, maxChunkBytes = 512 * 1024 } = {}) {
      const chunks = []
      let currentChunk = []
      let currentChunkBytes = 0

      rows.forEach((row) => {
        const rowSize = this.estimatePayloadSize(row)
        const wouldOverflow =
          currentChunk.length > 0 &&
          (
            currentChunk.length >= maxChunkRows ||
            currentChunkBytes + rowSize > maxChunkBytes
          )

        if (wouldOverflow) {
          chunks.push(currentChunk)
          currentChunk = []
          currentChunkBytes = 0
        }

        currentChunk.push(row)
        currentChunkBytes += rowSize
      })

      if (currentChunk.length) {
        chunks.push(currentChunk)
      }

      return chunks
    },
    getPersistedSiteOnlyResults (rows) {
      return rows.map((row) => {
        const persistedRow = { ...row }
        delete persistedRow.index
        delete persistedRow.keys
        return persistedRow
      })
    },
    handleGSCImportError (error, fallbackMessage = 'Could not save results.') {
      const errorMessage = this.getErrorMessage(error, fallbackMessage)

      console.log('GSC: Error')
      console.log(error)
      console.log(errorMessage)

      this.$notify({
        group: 'info',
        type: 'error',
        text: errorMessage,
      })

      return errorMessage
    },
    sortGoogleConsoleRows (rows) {
      return [...rows].sort((a, b) => {
        if (a.impressions == b.impressions) {
          const leftKeyword = a.keyword || a.keys?.[0] || ''
          const rightKeyword = b.keyword || b.keys?.[0] || ''

          if (leftKeyword < rightKeyword) return -1
          if (leftKeyword > rightKeyword) return 1
          return 0
        }

        return Number(b.impressions) - Number(a.impressions)
      })
    },
    async finalizeConsoleImport () {
      console.log('GSC: Wrapping up')

      let aggregatedRows = this.sortGoogleConsoleRows(this.googleConsoleDataAggregated)

      if (aggregatedRows.length > 30000) {
        aggregatedRows = aggregatedRows.slice(0, 30000)
      }

      const normalizedRows = aggregatedRows.map((d, index) => {
        return {
          ...d,
          keyword: d.keys[0],
          clicks: d.clicks,
          page: d.keys[1],
          ctr: Number(Number(d.ctr * 100).toFixed(2)),
          position: Number(Number(d.position).toFixed(2)),
          index: index,
        }
      })

      let results

      if (
        (this.selectedSubAccountRelation?.ownerId && this.selectedSubAccountRelation.ownerId === 27686)
        || (this.user?.id && this.user.id === 27686)
      ) {
        // do not calculate cannibalization for this specific user
        // has a website that results in very large cannibalization data preventing save
        results = [...normalizedRows]
      } else {
        results = this.calcForKeywordCannibalization(normalizedRows)
      }

      results = this.calcPageMetrics(results)

      const persistedResults = this.getPersistedSiteOnlyResults(results)
      const siteOnlyChunks = this.buildSaveChunks(persistedResults)
      const POresults = this.formatPOResults(normalizedRows)

      console.log('GSC: Saving data (1/2)')

      for (let i = 0; i < siteOnlyChunks.length; i++) {
        const chunk = siteOnlyChunks[i]
        console.log(
          `GSC: Saving data (1/2) chunk ${i + 1}/${siteOnlyChunks.length} (${chunk.length} rows, ~${this.estimatePayloadSize(chunk)} chars)`
        )

        const response = await this.$store.dispatch('saveKeywordWizardResult', {
          campaignId: this.campaign.id,
          locationCode: this.config.wizardLocation,
          languageCode: this.config.wizardLanguage,
          tabType: 'SITE_ONLY_EXIST',
          result: chunk,
          is_chunk: 1,
          is_first_chunk: i === 0,
          is_last_chunk: i === siteOnlyChunks.length - 1,
        })

        if (!(response.data && response.data.status == 'SUCCESS')) {
          throw response
        }
      }

      console.log('GSC: Saving data (2/2)')

      const POResponse = await this.$store.dispatch('saveKeywordWizardResult', {
        campaignId: this.campaign.id,
        locationCode: this.config.wizardLocation,
        languageCode: this.config.wizardLanguage,
        tabType: 'PAGE_OPPORTUNITY',
        result: POresults,
        shouldReturnResult: true,
      })

      if (!(POResponse.data && POResponse.data.status == 'SUCCESS')) {
        throw POResponse
      }

      const auditResponse = await this.$store.dispatch('updateSiteContentAudit', {
        auditId: this.activeAudit.id,
        body: {
          hasRetrievedFreshGSCData: 1,
        }
      })

      if (!(auditResponse.data && auditResponse.data.status == 'SUCCESS')) {
        throw auditResponse
      }

      this.$notify({
        group: 'info', type: 'success',
        text: 'Data successfully retrieved.'
      })
      this.storeWinsGscConnection()
      this.$emit('success');
      this.$emit('close');
    },
    /**
     * Mirror this step's selections into the server-side GscConnection so the
     * White Glove Settings/Wins tabs see the site as connected. Monitoring
     * stays OFF and the WG terms are not accepted here - the user enables
     * monitoring (with consent) from the Settings tab. Never blocks the
     * audit step; skipped when an active connection already exists so an
     * enabled monitoring/terms configuration is never downgraded.
     */
    storeWinsGscConnection () {
      if (this.hasActiveWinsConnection || !this.site || !this.site.id || !this.googleSiteUrl) return
      this.$store.dispatch('connectGsc', {
        siteId: this.site.id,
        body: {
          propertyUrl: this.googleSiteUrl,
          locationCode: this.config.wizardLocation || '',
          languageCode: this.config.wizardLanguage || '',
          countryEnabled: this.config.wizardIsCountryEnabled ? 1 : 0,
          datePeriod: (this.config.wizardPeriod && this.config.wizardPeriod.value) || 'last_28_days',
          monitoringEnabled: 0,
          termsAccepted: 0,
        },
      })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.hasActiveWinsConnection = true
          }
        })
        .catch((e) => {
          console.log('Could not store the GSC connection for Wins', e)
        })
    },
    getPopImpactScoreDataFromGoogleConsole () {
      const range = getOptionDate(this.availablePeriods.filter(x => x.value === 'last_3_months')[0])
      const endDate = range.endDate
      const startDate = range.startDate

      let filterList = []
      if (this.config.wizardIsCountryEnabled) {
        filterList.push({
          dimension: 'COUNTRY',
          expression: getCountryCode_ISO3(this.config.wizardLocation)
        })
      }

      const queryParams = {
        siteUrl: this.googleSiteUrl,
        resource: {
          startDate,
          endDate,
          dimensionFilterGroups: [
            {
              filters: filterList
            }
          ],
          dimensions: [
            'DATE'
          ],
        }
      }

      gapi.client.webmasters.searchanalytics.query(queryParams)
        .then((response) => {
          if (response.status == 200) {
            if (response.result?.rows?.length) {
              return this.$store.dispatch('updateSitePopImpactScore', {
                siteId: this.site.id,
                body: {
                  data: response.result.rows,
                }
              })
                .then((response) => {
                  if (response.data && response.data.status == 'SUCCESS') {
                    this.$store.commit('setSite', response.data.site)
                    return Promise.resolve()
                  } else {
                    this.$notify({
                      group: 'info', type: 'error',
                      text: 'Could not save Trust & Authority Score results.'
                    })
                    return Promise.reject(response)
                  }
                })
                .finally(() => {
                  this.submitting_pop_impact_score = false
                })
            }
          } else {
            this.$notify({
              group: 'info', type: 'error',
              text: 'no history found for Trust & Authority Score'
            })
            this.submitting_pop_impact_score = false
          }
        },
        () => {
          this.$notify({
            group: 'info', type: 'error',
            text: 'Failed to get data for Trust & Authority Score'
          })
          this.submitting_pop_impact_score = false
        })
    },
    getConsoleDataAggregated (startRow = 0) {
      const rowLimit = 25000
      const range = getOptionDate(this.config.wizardPeriod)
      const endDate = range.endDate
      const startDate = range.startDate

      let filterList = []
      if (this.config.wizardIsCountryEnabled) {
        filterList.push({
          dimension: 'COUNTRY',
          expression: getCountryCode_ISO3(this.config.wizardLocation)
        })
      }
      if (this.config.wizardIsBrandedEnabled && this.brandedTerms.length) {
        this.brandedTerms.slice(0, 10).forEach((keyword) => {
          filterList.push({
            dimension: 'QUERY',
            operator: 'excludingRegex',
            expression: `(?i)${keyword}`
          })
        })
      }

      const queryParams = {
        siteUrl: this.googleSiteUrl,
        resource: {
          startDate,
          endDate,
          dimensionFilterGroups: [
            {
              filters: filterList
            }
          ],
          dimensions: [
            'QUERY',
            'PAGE'
          ],
          rowLimit,
          startRow,
        }
      }

      gapi.client.webmasters.searchanalytics.query(queryParams)
        .then((response) => {
          if (response?.status == 200) {
            if (response.result?.rows) {
              console.log(`GSC: Got rows (${response.result?.rows.length})`)
              this.googleConsoleDataAggregated = [...this.googleConsoleDataAggregated, ...response.result.rows]
              if (response.result?.rows.length == rowLimit) {
                console.log('GSC: To next rows request')
                this.getConsoleDataAggregated(startRow + rowLimit)
              } else {
                return this.finalizeConsoleImport()
                  .then(() => {
                    return Promise.resolve()
                  })
                  .catch((e) => {
                    this.handleGSCImportError(e)
                    this.submitting_console_data = false
                  })
                  .finally(() => {
                    console.log('GSC: Completed')
                    this.googleConsoleDataAggregated = []
                    this.submitting_console_data = false
                  })
              }
            } else if (this.googleConsoleDataAggregated.length) {
              console.log('GSC: Final page returned no rows, wrapping accumulated data')
              return this.finalizeConsoleImport()
                .catch((e) => {
                  this.handleGSCImportError(e)
                  this.submitting_console_data = false
                })
                .finally(() => {
                  console.log('GSC: Completed')
                  this.googleConsoleDataAggregated = []
                  this.submitting_console_data = false
                })
            } else {
              return this.$store.dispatch('updateSiteContentAudit', {
                auditId: this.activeAudit.id,
                body: {
                  hasRetrievedFreshGSCData: 1,
                }
              }).then((updateResponse) => {
                if (!(updateResponse.data && updateResponse.data.status == 'SUCCESS')) {
                  return Promise.reject(updateResponse)
                }

                this.$notify({
                  group: 'info', type: 'success',
                  text: 'Completed step. Google Console did not return any data.'
                })
                this.storeWinsGscConnection()
                this.$emit('success')
                this.$emit('close')
              })
              .catch((e) => {
                this.handleGSCImportError(e, 'Could not update the audit after the GSC import completed.')
                this.submitting_console_data = false
              })
              .finally(() => {
                console.log('GSC: Completed. Google Console did not return any data')
                this.googleConsoleDataAggregated = []
                this.submitting_console_data = false
              })
            }
          } else {
            this.$notify({
              group: 'info', type: 'error',
              text: 'Got bad response from Google Console'
            })

            this.submitting_console_data = false
          }
        },
        (err) => {
          this.$notify({
            group: 'info', type: 'error',
            text: this.getErrorMessage(err, 'Failed to get data')
          })

          console.log(err)

          this.submitting_console_data = false
        })
    },
    calcForKeywordCannibalization (data) {
      const dataByKeywords = groupBy(data, 'keyword')

      forEach(dataByKeywords, (records) => {
        const count = records.length
        if (count == 1) return

        const avg = sumBy(records, 'impressions') / count

        forEach(records.slice(1), (el) => {
          const { impressions, index } = el
          const diff = (avg - impressions) / avg
          try {
            data[index]['cannibalizedKeywords'] = records.map(r => {
              return {
                page: r.page,
                keyword: r.keyword,
                clicks: r.clicks,
                impressions: r.impressions,
                ctr: r.ctr,
                position: r.position,
              }
            })
          } catch (eee) {
            console.log('Error on cannibalizedKeywords calculation', eee)
          }

          if (impressions > avg) {
            return data[index]['keywordCannibalizationText'] = 'high'
          }
          data[index]['keywordCannibalizationText'] = diff <= 0.25 ? 'medium' : 'low'
        })
      })
      return data
    },
    calcPageMetrics (data) {
      const dataByPage = groupBy(data, 'page')

      const metricsPerPage = Object.fromEntries(
        Object.entries(dataByPage).map(([key, records]) => [key, {
          totalPageImpressions: sumBy(records, 'impressions'),
          maxPagePosition: maxBy(records, 'position').position,
        }])
      )

      return data.map(row => (
        { ...row, ...metricsPerPage[row['page']] }
      ))
    },
    formatPOResults (googleData) {
      const google_data = googleData.map((d) => {
        return {
          ...d,
          clicks: d.clicks,
          ctr: Number(Number(d.ctr).toFixed(2)),
          position: Number(Number(d.position).toFixed(2))
        }
      })

      const google_data_by_page = groupBy(google_data, 'page')

      let results = Object.entries(google_data_by_page).map(([key, records]) => ({
        page: key,
        impressions: sumBy(records, 'impressions'),
        clicks: sumBy(records, 'clicks'),
        avgCTR: Number(Number(
          meanBy(records, 'ctr')
        ).toFixed(2)),
        avgPosition: Number(Number(
          meanBy(records, 'position')
        ).toFixed(2)),
        keywordsCount: records.length,
      }))

      return results
    },
    onStart () {
      this.submitting_console_data = true
      this.submitting_pop_impact_score = true

      this.googleConsoleDataAggregated = []
      this.getConsoleDataAggregated(0)

      this.getPopImpactScoreDataFromGoogleConsole()
    },
    applyDomainCampaignToWizard (campaign) {
      this.$store.commit('setCurrentCampaign', campaign)
      this.setConfig('wizardLanguage', campaign.languageCode)
      const loc = campaign.locationCode
      this.setConfig('wizardLocation', typeof loc === 'string' ? parseInt(loc, 10) : loc)
      if (!this.googleSiteUrl && this.config.wizardDomain) {
        this.googleSiteUrl = this.config.wizardDomain
      }
      if (!this.config.wizardPeriod) this.setConfig('wizardPeriod', this.availablePeriods[1])
    },
    siteLangAndLocationForCampaign () {
      const step0 = this.activeAudit?.formData?.step0 || {}
      let loc = step0.location_code
      if (loc === undefined || loc === null || loc === '') {
        loc = 2840
      }
      loc = typeof loc === 'string' ? parseInt(loc, 10) : loc
      const lang = step0.language_code || 'en'
      return { locationCode: loc, languageCode: lang }
    },
    loadDomainLevelCampaign () {
      const campaignId = this.activeAudit?.associatedDomainLevelCampaign
      this.$store.commit('showLoading')
      const finish = () => {
        this.$store.commit('hideLoading')
        this.emitReadiness()
      }
      if (!campaignId) {
        const { locationCode, languageCode } = this.siteLangAndLocationForCampaign()
        return this.$store.dispatch('createKeywordResearchCampaign', {
          campaignName: '',
          siteId: this.site.id,
          targetUrl: getHostName(this.site.baseURL),
          isDomainLevelCampaign: true,
          locationCode,
          languageCode,
        })
          .then(response => {
            if (response.data && response.data.status == 'SUCCESS') {
              this.applyDomainCampaignToWizard(response.data.campaign)
              return Promise.resolve()
            }
            const msg = (response.data && response.data.msg)
              ? response.data.msg
              : 'Could not create keyword research campaign for this site.'
            this.$notify({ group: 'info', type: 'error', text: msg })
            return Promise.reject(new Error(msg))
          })
          .finally(finish)
      }
      return this.$store.dispatch('getCampaignById', campaignId)
        .then(response => {
          if (response.data && response.data.status == 'SUCCESS') {
            this.applyDomainCampaignToWizard(response.data.campaign)
            return Promise.resolve()
          }
          return Promise.reject(new Error('Invalid response'))
        })
        .finally(finish)
    },
  },
}
